import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { KpiTile } from "@aurumiq/ui/composite/kpi-tile";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { dmyTime, int } from "@aurumiq/ui/lib/format";
import { Link } from "react-router";
import { PageHeader } from "@/app/page-header";
import { useMe } from "@/auth/use-permission";
import { DemoWorkstationCard } from "@/features/orgs/demo-workstation-card";
import { useDashboard } from "@/features/system/queries";
import { apiHooks } from "@/lib/api";

export function DashboardPage() {
  const health = apiHooks.useQuery("get", "/healthz", {}, { retry: false });
  const me = useMe();
  const dash = useDashboard();
  const d = dash.data ?? null;
  const pending = dash.isPending;
  const v = (n: number | undefined) => (pending ? "…" : n === undefined ? "—" : int(n));

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={
          d
            ? "Fleet-wide numbers, refreshed every minute."
            : "Global metrics appear once /admin/dashboard is live; this shows API reachability."
        }
      />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="API"
          value={
            health.isPending ? (
              "…"
            ) : health.isError ? (
              <StatusTag variant="err">UNREACHABLE</StatusTag>
            ) : (
              <StatusTag variant="ok">{health.data?.status?.toUpperCase() ?? "OK"}</StatusTag>
            )
          }
          hint={health.data?.version ? `v${health.data.version}` : undefined}
        />
        <KpiTile label="Signed in as" value={me.data?.name ?? "…"} hint={me.data?.role} />
        <KpiTile
          label="Active orgs"
          value={v(d?.orgs_active)}
          hint={d ? `${int(d.orgs_trial)} on trial` : "from /admin/dashboard"}
        />
        <KpiTile
          label="Devices seen 24h"
          value={v(d?.devices_seen_24h)}
          hint={d ? `${int(d.devices_active)} active` : "from /admin/dashboard"}
        />
        <KpiTile label="Invoices (7 d)" value={v(d?.invoices_7d)} />
        <KpiTile label="Verify scans (7 d)" value={v(d?.scans_7d)} />
        <KpiTile
          label="Crashes (7 d)"
          value={v(d?.crashes_7d)}
          className={d && d.crashes_7d > 0 ? "border-warning/50" : undefined}
        />
        <KpiTile
          label="Latest release"
          value={pending ? "…" : (d?.latest_release_version ?? "—")}
          hint={d ? `${int(d.staff_active)} active staff` : undefined}
        />
      </div>
      <DemoWorkstationCard />
      {d ? (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle>Recent audit</CardTitle>
            <Link to="/settings/audit" className="text-primary text-sm hover:underline">
              Full log
            </Link>
          </CardHeader>
          <CardContent>
            {d.recent_audit.length ? (
              <ul className="divide-y text-sm">
                {d.recent_audit.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1.5">
                    <span className="whitespace-nowrap text-muted-foreground text-xs tabular">
                      {dmyTime(a.at)}
                    </span>
                    <span className="font-mono text-xs">{a.action}</span>
                    <span>{a.actor_label ?? a.actor_id ?? "system"}</span>
                    {a.org_id ? (
                      <Link
                        to={`/orgs/${a.org_id}`}
                        className="font-mono text-primary text-xs hover:underline"
                      >
                        {a.org_id}
                      </Link>
                    ) : null}
                    {a.reason ? <span className="text-muted-foreground">— {a.reason}</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-sm">Nothing yet.</p>
            )}
          </CardContent>
        </Card>
      ) : null}
    </>
  );
}
